import Util from '../../../forecast-app/shared/util/util';
import RecordQuoteDecisionMutation from '../../../mutations/record_quote_decision_mutation';

/**
 * Mirrors the server-side reason cap and the column's VARCHAR(5000). Applied at the input via
 * TextArea's maxLength as well as re-checked in the graph layer and the company service.
 */
export const MAX_REASON_LENGTH = 5000;

export type QuoteDecision = 'APPROVED' | 'DECLINED' | 'REVISING';

/** Decisions whose reason is persisted. APPROVED discards one server-side; this mirrors that. */
const REASON_DECISIONS: readonly QuoteDecision[] = ['DECLINED', 'REVISING'];

export type QuoteDecisionInput = {
	quoteId: string;
	decision: QuoteDecision;
	clientContactId?: string | null;
	reason?: string | null;
};

/**
 * Records the decision. Always via Util.CommitMutation — never react-relay's commitMutation — so
 * the correlation-ID error modal, the logged-out handling and the single transient-network retry
 * all still apply.
 *
 * The first argument must be a mutation MODULE exposing commit(), not a graphql tag:
 * Util.CommitMutation calls mutationClass.commit(), and the module is also what supplies the CSRF
 * token and socket client id the backend requires.
 */
export function recordQuoteDecision(input: QuoteDecisionInput, onSuccess: () => void, onError?: () => void): void {
	// A reason is meaningless on an approve and is discarded server-side; nulling it here means
	// leftover textarea state never leaves the browser. A blank reason is null, not ''.
	const reason = REASON_DECISIONS.indexOf(input.decision) !== -1 && input.reason && input.reason.trim() ? input.reason : null;

	Util.CommitMutation(
		RecordQuoteDecisionMutation,
		{
			quoteId: input.quoteId,
			decision: input.decision,
			clientContactId: input.clientContactId || null,
			reason,
		},
		onSuccess,
		true,
		null,
		(transaction: unknown, onFailure: (t: unknown) => unknown) => {
			// Always fall through to the framework handler, or the readable rejection warning modal
			// (and its correlation id) is swallowed. It answers true when it has already scheduled
			// its own retry of this mutation - the panel has to stay disabled through that window,
			// or a second click decides the quote twice.
			const retryScheduled = onFailure(transaction);
			if (retryScheduled !== true && onError) onError();
		}
	);
}
